import React, { useEffect, useRef, useState } from "react";
import cx from "classnames";
import styles from "./styles.scss";
import { useQuery } from "@apollo/client";
import useRouter from "use-react-router";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import _ from "lodash";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";

import Icon from "components/Icon";
import Text from "components/Text";
import RoundButton from "components/RoundButton";
import DropdownMenu from "components/DropdownMenu";
import Button from "components/Button";
import { Mobile, useMobileQuery } from "components/Layout/Responsive";
import { helpCenter } from "support-articles";
import { getAppUrl, getBaseAppUrl, USER_ORGANIZATION_PLACEHOLDER, addQueries, omitQueries } from "utilities/routes";
import { logout } from "utilities/authentication";
import variables, { colors } from "styles/variables";
import SearchableBigSelect from "../../../../components/LegacySearchable/SearchableBigSelect";
import myOrganizationsQuery from "./myOrganizations.gql";
import organizationQuery from "./organization.gql";
import programGroupsQuery from "./programGroupsQuery.gql";
import { usesHashRouter } from "utilities/environment";

import userIcon from "assets/user-circle.svg";
import homeIcon from "assets/home.svg";
import secretIcon from "assets/secret.svg";
import { useUserInfo } from "modules/Dashboard/UserInfo";
import { UserLink } from "modules/Dashboard/UserLink";
import { NavHeaderOrganizationQueryVariables, NavHeaderOrganizationQuery, Maybe } from "gql-gen";
import { canSeeCompanySettings, isUserAtLeast, User } from "interfaces/user";
import { programsRouteName } from "../Programs";
import { EMBED_MODE, EmbedMode } from "utilities/embed";
import Spinner from "components/Spinner";
import chevronRightIcon from "assets/chevrons-right-regular.svg";

interface NavAction {
  text: string;
  onClick?: React.MouseEventHandler;
  href?: string;
  testId?: string;
}

export interface Tab {
  name: string;
  link?: string;
  remote?: boolean;
  disabled?: string | null;
  icon: any;
}

interface Props {
  children?: React.ReactNode;
  action?: NavAction;
  renderAction?: React.ReactNode;
  tabs?: Tab[];
  client?: any;
}
export default function NavHeader(props: Props) {
  const {
    match: {
      params: { id, orgId, page, programGroupId },
    },
    location,
    history,
  } = useRouter<{ id: string; orgId: string; page: string; programGroupId: string }>();

  const { data: userOrgs, loading: organizationsLoading } = useQuery(myOrganizationsQuery, { variables: { id } });
  const {
    data: programGroupsData,
    loading: programGroupsLoading,
    refetch: refetchProgramGroups,
  } = useQuery(programGroupsQuery, { fetchPolicy: "network-only" });

  const { me: navMe, loading: userInfoLoading } = useUserInfo();
  const newNav = !!navMe?.organizations?.find(o => o.id === orgId)?.newNav;

  const { data: orgData, loading: orgDataLoading } = useQuery<
    NavHeaderOrganizationQuery,
    NavHeaderOrganizationQueryVariables
  >(organizationQuery, { variables: { orgId }, skip: orgId === USER_ORGANIZATION_PLACEHOLDER });

  function renderTabs(isMobile: boolean) {
    const { tabs } = props;
    const tabParam = new URLSearchParams(location.search).get("tab");
    const activeTab = tabs && (
      tabParam
        ? tabs.find(tab => tab.name.toLowerCase() === tabParam)
        : tabs.find(tab => tab.link && location.pathname.includes(tab.link))
    );

    const items =
      tabs &&
      tabs.map((tab, index) => {
        const active = tab === activeTab;

        const label = isMobile ? (
          <div className={cx(styles.mobileTab)}>
            <Icon src={tab.icon} fill={active ? variables.gray1 : newNav ? variables.gray3 : variables.white} width={30} />
            {(active || newNav) && (
              <Text
                size={isMobile ? 10 : 12}
                font={"wes"}
                bold
                color={active ? "gray1" : newNav ? "gray3" : "white"}
                className={cx(tab.disabled ? styles.disabledLabel : styles.label)}
                tooltip={tab.disabled || undefined}
                transform={"uppercase"}
                letterSpacing={1.2}
              >
                {tab.name}
              </Text>
            )}
          </div>
        ) : (
          <Text
            size={newNav ? 20 : 12}
            font={newNav ? active ? "wes" : "wesR" : "wes"}
            bold
            color={active ? "gray1" : newNav ? "gray1" : "white"}
            className={cx(tab.disabled ? styles.disabledLabel : styles.label)}
            tooltip={tab.disabled || undefined}
            transform={newNav ? "capitalize" : "uppercase"}
            letterSpacing={1.2}
          >
            {tab.name}
          </Text>
        );

        return (
          <div
            className={cx(styles.tab, active && styles.activeTab, newNav ? styles.newTab : "")}
            key={index}
            data-test={`${tab.name.toLowerCase()}-tab`}
          >
            {tab.disabled ? (
              label
            ) : tab.remote ? (
              <a
                href={!tab.disabled && tab.link ? tab.link : "javascript:void()"}
                target="_blank"
                rel="noopener noreferrer"
              >
                {label}
              </a>
            ) : (
              <Link
                to={{
                  pathname: tab.link || "",
                  search: newNav
                    ? addQueries(omitQueries(location.search, ["f"]), { tab: tab.name.toLowerCase() })
                    : location.search,
                }}
              >{label}</Link>
            )}
          </div>
        );
      });

    return (
      items &&
      items.length > 0 && (
        <div className={cx(styles.tabBackground, newNav ? styles.tabNoBackground : "")}>
          <div className={styles.contentWrapper}>
            <div className={newNav ? styles.newNavTabs : styles.tabs}>{items}</div>
          </div>
        </div>
      )
    );
  }

  if (EMBED_MODE !== EmbedMode.NONE) {
    return null;
  }

  if (userInfoLoading) {
    return (
      <>
        <div className={cx(styles.topNav, styles.topNavShadow)}>
          <div className={styles.contentWrapper}>
            <div className={styles.skeletonBar} style={{ width: 140, height: 14 }} />
            <div className={styles.skeletonBar} style={{ width: 60, height: 14 }} />
          </div>
        </div>
        <div className={styles.children}>
          <div className={styles.contentWrapper}>
            <div className={styles.skeletonBar} style={{ width: 200, height: 28 }} />
            <div className={styles.skeletonBar} style={{ flex: 2, height: 28 }} />
          </div>
        </div>
        {props.tabs && props.tabs.length > 0 && (
          <div className={styles.tabBackground}>
            <div className={styles.contentWrapper}>
              <div className={styles.tabs}>
                {props.tabs.map((_, i) => (
                  <div key={i} className={styles.tab}>
                    <div className={styles.skeletonBar} style={{ width: 70, height: 12 }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <Mobile>
        {mobile => (
          <>
            {newNav ? (
              <NewNav
                orgData={orgData}
                orgDataLoading={orgDataLoading}
                action={props.action}
                renderAction={props.renderAction}
                refetchProgramGroups={refetchProgramGroups}
              />
            ) : (
              <TopNav
                mobile={mobile}
                orgData={orgData}
                orgDataLoading={orgDataLoading}
                refetchProgramGroups={refetchProgramGroups}
                action={props.action}
              />
            )}
            {props.children ? (
              <div className={cx(styles.children, newNav ? styles.newNavChildren : "")}>
                <div className={cx(newNav ? styles.contentWrapperPickers : styles.contentWrapper, mobile && styles.pickers)}>
                  {newNav && !mobile ? <span>Filtered to: </span> : null}
                  {!organizationsLoading && userOrgs?.me?.organizations?.length > 1 && !newNav && (
                    <SearchableBigSelect
                      testId={"navHeader.organizationSelector"}
                      className={styles.organizationPicker}
                      inputClassName={styles.orgInput}
                      clearSearchOnSelect={true}
                      items={userOrgs.me.organizations}
                      selectedItem={orgId}
                      onChange={item => {
                        if (usesHashRouter()) {
                          window.location.href = `#/${item.id}`;
                          window.location.reload();
                        } else {
                          window.location.href = `/${item.id}`;
                        }
                      }}
                      optionsYOffset={1}
                      selectProps={{
                        name: "Organization",
                        compact: mobile,
                      }}
                    />
                  )}
                  {!programGroupsLoading &&
                    orgId &&
                    location.pathname.includes("programs") &&
                    orgData?.organization?.programGroupsVisible && (
                      <SearchableBigSelect
                        testId={"navHeader.programGroupSelector"}
                        className={cx(styles.programGroupPicker, newNav && styles.newNavProgramGroupPicker)}
                        inputClassName={cx(styles.orgInput, newNav && styles.newNavOrgInput)}
                        clearSearchOnSelect={true}
                        items={[{ name: newNav ? "All Program Groups" : "All", id: "all" }, ...programGroupsData?.programGroups]}
                        selectedItem={programGroupId}
                        onChange={item => {
                          if (item.id === "all") {
                            history.replace({
                              ...location,
                              pathname: `/${orgId}/program-group/all/${programsRouteName}/${page ? "/" + page : ""}`,
                            });
                          } else {
                            const programIds = item.programIds.join(",");
                            history.replace({
                              ...location,
                              pathname: `/${orgId}/program-group/${item.id}/${programsRouteName}/${page ? "/" + page : ""
                                }`,
                              search: addQueries(location.search, { program: programIds }),
                            });
                          }
                        }}
                        optionsYOffset={1}
                        selectProps={{
                          name: newNav ? "" : "Program Group",
                          compact: mobile,
                          inverse: newNav
                        }}
                      />
                    )}
                  <div className={cx(styles.programPicker, newNav && styles.newProgramPicker)}>{props.children}</div>
                </div>
              </div>
            ) : !newNav ? (
              <div className={styles.children}>
                <div className={styles.contentWrapper} />
              </div>
            ) : null}
            {renderTabs(mobile)}
          </>
        )}
      </Mobile>
    </>
  );
}

interface TopNavProps {
  mobile: boolean;
  orgData?: NavHeaderOrganizationQuery;
  refetchProgramGroups: () => void;
  orgDataLoading: boolean;
  action?: NavAction;
}

function TopNav(props: TopNavProps) {
  const { mobile, orgData, orgDataLoading, refetchProgramGroups, action } = props;
  const { me, impersonator } = useUserInfo();
  const { t } = useTranslation();
  const {
    match: {
      params: { orgId },
    },
  } = useRouter<{ orgId: string }>();

  return (
    <div className={cx(styles.topNav, styles.topNavShadow)}>
      <div className={styles.contentWrapper}>
        <div className={styles.whiteLeft}>
          <div className={styles.orgAndLogo}>
            {!orgDataLoading && !mobile && (
              <Text
                size={12}
                color={"gray1"}
                font={"wes"}
                letterSpacing={1.2}
                transform={"uppercase"}
                className={styles.alignOrg}
                lineHeight={10}
              >
                {_.get(orgData, "organization.name")}
              </Text>
            )}
          </div>

          {me && impersonator && (
            <div className={styles.impersonating}>
              <Icon src={secretIcon} size={14} fill={colors.purple} />
              <UserLink id={impersonator.id}>
                <Text size={12} font="wes" color="purple" bold lineHeight={10} left="xs">
                  {impersonator.firstName[0]} {impersonator.lastName[0]}
                </Text>
              </UserLink>
              <Text size={12} font="wes" color="purple" bold lineHeight={10} left="xs">
                as
              </Text>
              <UserLink id={me.id}>
                <Text size={12} font="wes" color="purple" bold lineHeight={10} left="xs">
                  {me.firstName} {me.lastName[0]}. ({me.organizationUser?.type})
                </Text>
              </UserLink>
            </div>
          )}
        </div>
        <div className={styles.accountAndGigs} style={{ display: "flex" }}>
          <DropdownMenu
            showArrow={!mobile}
            arrowColor={"gray4"}
            menuClassName={styles.menu}
            renderMenu={() => <TopNavMenuItems orgId={orgId} orgData={orgData} me={me} />}
            testId={"navHeader.settings"}
          >
            {mobile ? (
              <RoundButton>
                <Icon src={userIcon} size={42} fill={variables.gray4} />
              </RoundButton>
            ) : (
              <Button>
                <Text
                  size={12}
                  color={"gray1"}
                  font={"wes"}
                  letterSpacing={1.5}
                  transform={"uppercase"}
                  className={styles.alignOrg}
                  lineHeight={10}
                >
                  {t("menu")}
                </Text>
              </Button>
            )}
          </DropdownMenu>
          {action ? (
            <CustomAction
              onClick={() => (location.pathname.includes("settings") ? refetchProgramGroups : null)}
              action={action}
              newNav={!!orgData?.organization.newNav}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

interface TopNavMenuItemsProps {
  orgId: string;
  orgData?: NavHeaderOrganizationQuery;
  me: User | null;
  children?: React.ReactNode;
}

function TopNavMenuItems({ orgId, orgData, me, children }: TopNavMenuItemsProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.menuContent}>
      {children}
      <Button className={styles.menuItem} href={getAppUrl("go", `${orgId || "_"}`)}>
        <Text size={12} font={"wes"} bold>
          {t("goToGo")}
        </Text>
      </Button>
      {me?.isAdmin && (
        <Button className={styles.menuItem} href={getAppUrl("admin")}>
          <Text size={12} font={"wes"} bold>
            {t("goToAdmin")}
          </Text>
        </Button>
      )}

      <Button
        className={styles.menuItem}
        href={getAppUrl("accounts", `${orgId || "_"}/my-account`, { ref: "dashboard" })}
      >
        <Text size={12} font={"wes"} bold>
          {t("myaccount")}
        </Text>
      </Button>

      {me?.organizationUser &&
        (orgData?.organization?.isWorkspace === false || me.isAdmin || me?.organizationUser?.type === "owner") &&
        canSeeCompanySettings(me?.organizationUser?.type) && (
          <Link to={{ ...location, pathname: `/${orgId || ""}/settings` }}>
            <Button className={styles.menuItem}>
              <Text size={12} font={"wes"} bold>
                {t("mycompany")}
              </Text>
            </Button>
          </Link>
        )}
      {isUserAtLeast(me, "manager") && (
        <Link to={{ ...location, pathname: `/${orgId || ""}/invoices`, search: "" }}>
          <Button className={styles.menuItem}>
            <Text size={12} font={"wes"} bold>
              {t("invoices")}
            </Text>
          </Button>
        </Link>
      )}
      <a href="mailto:support@gopinata.com" className={cx(styles.menuItem, styles.mailto)}>
        <Text size={12} font={"wes"} bold>
          {t("chatWithUs")}
        </Text>
      </a>
      <Button className={styles.menuItem} href={helpCenter}>
        <Text size={12} font={"wes"} bold>
          {t("helpCenter")}
        </Text>
      </Button>
      <Button className={styles.menuItem} onClick={() => logout()}>
        <Text size={12} font={"wes"} bold>
          {t("logOut")}
        </Text>
      </Button>
    </div>
  );
}

interface CustomActionProps {
  action: NavAction;
  onClick?: () => void;
  newNav: boolean;
}

function CustomAction({ onClick, action, newNav }: CustomActionProps) {
  const { text, onClick: onAction, href, testId } = action;

  let actionView = (
    <Button
      testId={testId}
      kind="primaryGradient"
      className={newNav ? styles.topNavActionButton : ""}
      onClick={e => {
        onClick && onClick();
        onAction && onAction(e);
      }}
    >
      <Text color={"white"} font="wes">
        {text}
      </Text>
    </Button>
  );

  if (href) {
    actionView = <Link to={href}>{actionView}</Link>;
  }

  return <div>{actionView}</div>;
}

interface NewNavProps {
  orgData?: NavHeaderOrganizationQuery;
  orgDataLoading: boolean;
  action?: NavAction;
  renderAction?: React.ReactNode;
  refetchProgramGroups?: () => void;
}

function NewNav({ orgData, orgDataLoading, action, renderAction, refetchProgramGroups }: NewNavProps) {
  const { me, loading: loadingMe, impersonator } = useUserInfo();
  const isMobile = useMobileQuery();
  const branding = orgData?.organization?.primaryColor ?? "#00DEB0";
  const { location } = useRouter();

  if (!orgData || orgDataLoading || loadingMe) {
    return (
      <div style={{ "--primary-color": branding } as React.CSSProperties} className={cx(styles.newTopNav, styles.newTopNavLoading)}>
        <Spinner color="white" />
      </div>
    );
  }

  return (
    <div style={{ "--primary-color": branding } as React.CSSProperties} className={styles.newNavWrapper}>
      <Logo
        url={orgData.organization.logoUrl}
        orgId={orgData.organization.id}
      />
      <div className={styles.newTopNav}>
        <Breadcrumbs orgId={orgData.organization.id} />
        <div className={styles.navRight}>
          {impersonator && me ? (
            <div>
              <Icon src={secretIcon} size={14} fill={colors.white} />
              <UserLink id={me.id}>
                <Text size={isMobile ? 14 : 16} font="wes" color="white">
                  {me.firstName} {me.lastName[0]}. ({me.organizationUser?.type})
                </Text>
              </UserLink>
            </div>
          ) : null}

          <button
            className={styles.homeButton}
            onClick={() => (window.location.href = getAppUrl("theApp") + orgData.organization.id)}
            aria-label="Go to home"
          >
            <Icon src={homeIcon} width={24} />
          </button>

          {renderAction && !isMobile ? renderAction : action && !isMobile ? (
            <CustomAction
              onClick={() => (location.pathname.includes("settings") ? refetchProgramGroups : null)}
              action={action}
              newNav={!!orgData?.organization.newNav}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

interface LogoProps {
  url: Maybe<string | undefined>;
  orgId: string;
}

const routeMatchers: [RegExp, string][] = [
  [/\/gigs\/calendar/, "Calendar"],
  [/\/gigs/, "Task Management"],
  [/\/reports/, "Reports"],
  [/\/settings/, "Settings"],
  [/\/overview/, "Overview"],
  [/\/financials/, "Financials"],
  [/\/invoices/, "Invoicing"],
];

function Logo(props: LogoProps) {
  const appUrl = getAppUrl("theApp");
  return (
    <a className={styles.logo} href={appUrl + props.orgId}>
      <div className={styles.logoIcon}>
        {props.url ? (
          <img alt="company-logo" src={props.url} />
        ) : (
          <Icon src={homeIcon} width={24} />
        )}
      </div>
    </a>
  );
}

function Breadcrumbs({ orgId }: { orgId: string }) {
  const { location } = useRouter();
  const appUrl = getAppUrl("theApp");
  const breadcrumb = pipe(
    routeMatchers,
    A.findFirst(([regex]) => regex.test(location.pathname)),
    O.map(([, title]) => title),
    O.getOrElse(() => ""),
  );

  if (!breadcrumb) return null;

  return (
    <div className={styles.breadcrumbs}>
      <button onClick={() => (window.location.href = appUrl + orgId)}>
        <Text font="lato" size={16} color="white">
          Home
        </Text>
      </button>
      <div className={styles.breadcrumbChevron}>
        <Icon src={chevronRightIcon} fill={colors.white} height={10} />
      </div>
      <Text font="wes" bold size={16} color="white" className={styles.breadcrumbContainer}>
        {breadcrumb}
      </Text>
    </div>
  );
}
